/**
 * Auth Service
 * Handles authentication operations with the backend API
 */

import { Effect } from "effect";
import type { AppError } from "@link-storage/common";
import type { UserResponse, AuthState } from "@link-storage/contracts";
import { fetchJson, post } from "./apiClient";

/**
 * Session token storage key
 */
const SESSION_TOKEN_KEY = "session_token";

/**
 * Get session token from localStorage
 */
export const getSessionToken = (): string | null => {
  return localStorage.getItem(SESSION_TOKEN_KEY);
};

/**
 * Set session token in localStorage
 */
export const setSessionToken = (token: string): void => {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
};

/**
 * Clear session token from localStorage
 */
export const clearSessionToken = (): void => {
  localStorage.removeItem(SESSION_TOKEN_KEY);
};

/**
 * API Response types
 */
interface AuthMeResponse {
  isAuthenticated: boolean;
  user: UserResponse | null;
}

interface GoogleAuthUrlResponse {
  url: string;
}

interface LogoutResponse {
  success: boolean;
}

/**
 * Get current authentication state
 */
export const getAuthState = (): Effect.Effect<AuthState, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(fetchJson<AuthMeResponse>("/auth/me", { method: "GET" }));
    return {
      isAuthenticated: response.isAuthenticated,
      user: response.user,
    };
  });

/**
 * Get Google OAuth login URL
 */
export const getGoogleAuthUrl = (): Effect.Effect<string, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(fetchJson<GoogleAuthUrlResponse>("/auth/google", { method: "GET" }));
    return response.url;
  });

/**
 * Logout user
 */
export const logout = (): Effect.Effect<void, AppError> =>
  Effect.gen(function* (_) {
    yield* _(post<LogoutResponse>("/auth/logout", {}));
  });

/**
 * Login with Google
 * Redirects to Google OAuth page
 */
export const loginWithGoogle = async (): Promise<void> => {
  const result = await Effect.runPromise(getGoogleAuthUrl());
  window.location.href = result;
};
